import { ApiError } from '../../api/errors';
import { request } from '../../api/http';
import { asArray, unwrap } from '../../api/normalise';
import type { Id } from '../../api/types';

/**
 * Versions for one project.
 *
 * 🔴 THE ENDPOINT MAY NOT EXIST YET. At the commit this was written against, `routes/projects.ts`
 * serves `/projects`, `/projects/:id`, `/projects/:id/members` and `/projects/:id/sprints` — and NO
 * `/projects/:id/versions`. Another agent is adding it.
 *
 * ⛔⛔ SO THIS DOES NOT MOCK IT. A fixture kinder than the API is exactly how the last defect reached
 * production here (`dev/mock-api.mjs` invented a top-level-only filter, and the backlog nested
 * perfectly by doing nothing). When the endpoint is absent the view SAYS the endpoint is absent,
 * names it, and shows only what it can honestly derive from tickets — labelled as derived.
 *
 * ⭐ A 404 is TWO different facts here and the API lets us tell them apart:
 *   · `{"error":"not_found","message":"no such route"}`   → the endpoint is not deployed
 *   · `{"error":"not_found","message":"project not found"}` → the id is wrong
 * They lead to completely different actions, so they are not collapsed into "Not found".
 */

export type VersionState = 'unreleased' | 'released' | 'archived';

/**
 * One row of `GET /api/v1/projects/:id/versions`, read off the API's `services/versions.ts`.
 *
 * ⚠️ `project_id` is NOT on this response — the project is in the path. Typing it as required would
 * have compiled and put `undefined` on screen.
 * ⚠️ `state` is `string` on the wire, not a narrowed union: the CHECK constraint allows three values
 * today and a migration may add a fourth. `isKnownState` narrows it where that matters, and an
 * unknown value is shown as ITSELF rather than bucketed into `unreleased` — which would report a
 * shipped release as unshipped.
 * ⭐ `release_date` is `YYYY-MM-DD` because the API casts the DATE `::text`. ⛔ Never `new Date()`
 * arithmetic on it: that cast exists because node-postgres parses a DATE at LOCAL midnight, and in
 * Asia/Bangkok the day before is what a client would have read.
 */
export interface Version {
  id: Id;
  name: string;
  description: string | null;
  release_date: string | null;
  state: string;
  /**
   * ⭐⭐ THE AUTHORITATIVE COUNTS, over the WHOLE project rather than over the tickets this client
   * happened to load. The API computes both, separately and never summed, for the same reason this
   * app keeps them apart: `version_ticket_count` is planned-FOR and `fix_version_ticket_count` is
   * shipped-IN, and one number for both makes "what is left" unanswerable.
   * ⚠️ Optional, because a deployment older than the API commit that added them will not send them —
   * and a missing count must read as "we do not have it", not as zero.
   */
  version_ticket_count?: number;
  fix_version_ticket_count?: number;
}

export type VersionsResult =
  | { kind: 'ok'; versions: Version[] }
  /** The route is not deployed. The path is carried so the UI can name it rather than hand-wave. */
  | { kind: 'endpoint-missing'; path: string };

export function versionsPath(projectId: Id): string {
  return `/projects/${projectId}/versions`;
}

function isMissingRoute(error: ApiError): boolean {
  if (error.kind !== 'not_found') return false;
  const reason = (error.reason ?? '').toLowerCase();
  // ⚠️ Only the API's own not-found handler says this. A project that does not exist says
  // "project not found" and is a real 404 that must reach the caller.
  return reason.includes('no such route');
}

export async function fetchVersions(
  projectId: Id,
  signal?: AbortSignal,
): Promise<VersionsResult> {
  const path = versionsPath(projectId);
  try {
    const body = await request<unknown>(path, { signal });
    return { kind: 'ok', versions: asArray<Version>(body, path) };
  } catch (err) {
    if (err instanceof ApiError && isMissingRoute(err)) {
      return { kind: 'endpoint-missing', path: `/api/v1${path}` };
    }
    throw err;
  }
}

/**
 * ⚠️ A version's own `state` is authoritative, but old rows may carry anything the CHECK allows and a
 * future migration may add a state this build does not know. An unknown value is shown as itself
 * rather than bucketed into "unreleased", which would report a shipped release as unshipped.
 */
export function isKnownState(state: string): state is VersionState {
  return state === 'unreleased' || state === 'released' || state === 'archived';
}

// ── The write surface (PH-28) ─────────────────────────────────────────────────────────────────────

/**
 * 🔴🔴 THE WHOLE LIFECYCLE HAS EXISTED ON THE API "FOR AGES" AND THIS PAGE HAD NONE OF IT (PH-28).
 * `features/versions/` contained zero POSTs: `/versions?project=16` rendered read-only, and a release
 * could be created and shipped only with curl.
 *
 * Read off `tbs-planhub-api/src/routes/versions.ts`, not guessed:
 *
 *   POST  /api/v1/projects/:id/versions   {name, description?, releaseDate?}  → 201 {version}
 *   POST  /api/v1/versions/:id/release    {releaseDate?}                      → 200 {version}
 *   POST  /api/v1/versions/:id/archive    (no body)                           → 200 {version}
 *   POST  /api/v1/versions/:id/unarchive  (no body)                           → 200 {version}
 *
 * ⛔ `state` IS NOT AN INPUT ANYWHERE. The API refuses to model it as one — *"a version is born
 * `unreleased`; the two state changes are their own endpoints because each has its own refusals and
 * each is an EVENT someone can be told about"*. So there is deliberately no `setVersionState` here;
 * a client function for it could not exist without inventing an endpoint.
 *
 * ⛔ AND THERE IS NO DELETE. The API has no `DELETE /versions/:id` at all — archiving is how a
 * release leaves the pickers, and it is reversible. A delete button here would have nothing to call.
 *
 * ⚠️ `releaseDate` is `^\d{4}-\d{2}-\d{2}$` at the API (`const isoDate`). It travels as the STRING an
 * `<input type="date">` already holds; ⛔ no `Date` is constructed anywhere on this path. A
 * `new Date(v).toISOString()` produces `2026-09-01T00:00:00.000Z`, which that regex refuses outright
 * and which comes back as the fixed generic `the request body or query was not valid`.
 *
 * ⭐ Every one of these returns the SERVER's version row, and every caller uses it rather than
 * predicting the outcome. `unarchive` is exactly why: its destination is DERIVED from `release_date`,
 * so a released version that was archived comes back `released`, NOT `unreleased`.
 */

export interface CreateVersionBody {
  name: string;
  description?: string;
  /** `YYYY-MM-DD`. ⚠️ A PLAN, not a fact — setting it does NOT release the version. */
  releaseDate?: string;
}

export async function createVersion(
  projectId: Id,
  body: CreateVersionBody,
): Promise<Version> {
  const path = versionsPath(projectId);
  return unwrap<Version>(await request<unknown>(path, { method: 'POST', body }), 'version', path);
}

/**
 * `unreleased` → `released`, stamping the day it actually went out.
 *
 * ⚠️ AN OMITTED DATE MEANS TODAY — `COALESCE($2::date, current_date)` at the API, in the SERVER's
 * timezone. That is why the dialog sends an explicit date: "today" computed on a Bangkok laptop and
 * "today" computed by the database are not always the same day, and the release date is a number
 * people read off a report months later.
 */
export async function releaseVersion(id: Id, releaseDate: string | null): Promise<Version> {
  const path = `/versions/${id}/release`;
  return unwrap<Version>(
    await request<unknown>(path, {
      method: 'POST',
      body: releaseDate ? { releaseDate } : {},
    }),
    'version',
    path,
  );
}

export async function archiveVersion(id: Id): Promise<Version> {
  const path = `/versions/${id}/archive`;
  return unwrap<Version>(await request<unknown>(path, { method: 'POST', body: {} }), 'version', path);
}

/**
 * `archived` → back where it was.
 * ⚠️ NOT necessarily `unreleased`. The API derives it: `CASE WHEN release_date IS NULL THEN
 * 'unreleased' ELSE 'released' END`. Verified in production — unarchiving a released version returns
 * `released`. ⇒ The UI must render the row the server sends and never predict this.
 */
export async function unarchiveVersion(id: Id): Promise<Version> {
  const path = `/versions/${id}/unarchive`;
  return unwrap<Version>(
    await request<unknown>(path, { method: 'POST', body: {} }),
    'version',
    path,
  );
}
